import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Modal, ModalBody } from "react-bootstrap";
import { useForm, useFieldArray } from "react-hook-form";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import { CallingV2 } from "../../utils/APIClient";
import { triggerAlert } from "../../utils";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Calling V2 → IVR Templates.
 *
 * Reusable IVRConfig layouts (mode + routes). Customers can opt into one
 * of these when binding a DID, or define their routes ad-hoc on the
 * inbound page.
 */
export default function CallingV2IVRTemplates() {
  document.title = "Digital Employees — V2 IVR Templates";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("ai");

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
    defaultValues: { name: "", mode: "ai", prompt: "", routes: [{ digit: "1", target: "" }], is_active: true },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "routes" });
  const watchedMode = watch("mode");
  useEffect(() => { setMode(watchedMode); }, [watchedMode]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await CallingV2.listIVRTemplates();
      const list = r?.data?.results || [];
      list.forEach((v, i) => { v.Num = i + 1; });
      setRows(list);
    } catch (e) {
      setRows([]);
      triggerAlert("error", "Oops...", "Failed to load IVR templates.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    reset({ name: "", mode: "ai", prompt: "", routes: [{ digit: "1", target: "" }], is_active: true });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const routeArr = Object.entries(row.routes || {}).map(([digit, target]) => ({ digit, target }));
    reset({
      name: row.name || "",
      mode: row.mode || "ai",
      prompt: row.prompt || "",
      routes: routeArr.length ? routeArr : [{ digit: "1", target: "" }],
      is_active: !!row.is_active,
    });
    setShowModal(true);
  };

  const onSubmit = async (data) => {
    const routes = {};
    if (data.mode === "dtmf") {
      for (const r of (data.routes || [])) {
        const d = (r.digit || "").trim();
        const t = (r.target || "").trim();
        if (!d || !t) continue;
        if (!/^\d+$/.test(d)) {
          triggerAlert("error", "Oops...", `Route key must be digits, got "${d}".`);
          return;
        }
        routes[d] = t;
      }
      if (Object.keys(routes).length === 0) {
        triggerAlert("error", "Oops...", "DTMF mode requires at least one route.");
        return;
      }
    }

    const payload = {
      name: data.name.trim(),
      mode: data.mode,
      prompt: data.prompt || "",
      routes,
      is_active: !!data.is_active,
    };

    setSubmitting(true);
    try {
      if (editing) {
        await CallingV2.updateIVRTemplate(editing.id, payload);
        triggerAlert("success", "Saved", "IVR template updated.");
      } else {
        await CallingV2.createIVRTemplate(payload);
        triggerAlert("success", "Created", "IVR template added.");
      }
      setShowModal(false);
      reset();
      fetchRows();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      triggerAlert("error", "Oops...", msg);
    } finally { setSubmitting(false); }
  };

  const toggleStatus = async (row) => {
    try {
      await CallingV2.updateIVRTemplate(row.id, { is_active: !row.is_active });
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Could not update status."); }
  };

  const onDelete = async (row) => {
    const ok = await confirm({
      title: "Delete IVR template?",
      message: `Delete IVR template "${row.name}"?`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await CallingV2.deleteIVRTemplate(row.id);
      triggerAlert("success", "Deleted", "Template removed.");
      fetchRows();
    } catch (e) { triggerAlert("error", "Oops...", "Delete failed."); }
  };

  const columns = [
    { name: "S.No", width: "70px", selector: (r) => r.Num, cell: (r) => <span>{r.Num}</span> },
    { name: "Name", sortable: true, selector: (r) => r.name, cell: (r) => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    {
      name: "Mode", sortable: true, selector: (r) => r.mode, width: "120px",
      cell: (r) => <span className={`badge ${r.mode === "dtmf" ? "bg-info" : "bg-success"}`}>{r.mode.toUpperCase()}</span>,
    },
    {
      name: "Routes", selector: (r) => Object.keys(r.routes || {}).length,
      cell: (r) => <span>{Object.keys(r.routes || {}).length} route(s)</span>,
    },
    {
      name: "Status", sortable: true, selector: (r) => r.is_active, width: "110px",
      cell: (r) => r.is_active
        ? <span className="badge bg-success">Active</span>
        : <span className="badge bg-danger">Inactive</span>,
    },
    {
      name: "Action",
      cell: (r) => (
        <div className="d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => openEdit(r)}>Edit</button>
          <button className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`} onClick={() => toggleStatus(r)}>
            {r.is_active ? "Pause" : "Activate"}
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(r)}>Delete</button>
        </div>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: { style: { backgroundColor: "#064c77", borderBottom: "1px solid #dee2e6", minHeight: "52px" } },
    headCells: { style: { color: "#ffffff", fontSize: "13px", fontWeight: "700", textTransform: "uppercase" } },
    rows: { style: { minHeight: "60px", backgroundColor: "#ffffff", "&:hover": { backgroundColor: "#f8f9fa" } } },
    cells: { style: { fontSize: "14px", color: "#495057", borderBottom: "1px solid #f1f3f5" } },
    pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
  };

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content" style={{ backgroundColor: "#ededed" }}>
          <div className="container-fluid">
            <PageHeader mainHeading="IVR Templates" heading="Calling V2 / IVR Templates" />
            <div className="row"><div className="col-12"><div className="card"><div className="card-body">
              <div className="alert alert-info py-2 mb-3">
                IVR templates describe how a call is routed once it lands. <strong>AI</strong> mode uses
                the entry agent's prompt to interpret what the caller says. <strong>DTMF</strong> mode
                routes by digit press (e.g. "Press 1 for sales").
              </div>
              <div className="row align-items-center mb-3">
                <div className="col-md-8"></div>
                <div className="col-md-4 text-end">
                  <button className="btn btn-primary w-md btn-md" onClick={openCreate}>+ New IVR Template</button>
                </div>
              </div>
              {!loading ? (
                <DataTable columns={columns} data={rows} pagination fixedHeader fixedHeaderScrollHeight="600px" persistTableHead customStyles={customStyles}
                  noDataComponent={<div style={{ padding: 24 }}>No IVR templates yet.</div>} />
              ) : <div className="loader-overlay text-white"><Loader /></div>}
            </div></div></div></div>
          </div>
        </div>
        <Footer />
      </div>

      <Modal size="lg" show={showModal} onHide={() => !submitting && setShowModal(false)} backdrop="static" keyboard={!submitting}>
        <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
          <h1 className="modal-title fs-5">{editing ? "Edit" : "New"} IVR Template</h1>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input className="form-control" {...register("name", { required: "Required" })} />
                {errors.name && <div className="text-danger font_14 mt-1">{errors.name.message}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Mode</label>
                <select className="form-select" {...register("mode")}>
                  <option value="ai">AI — natural-language routing</option>
                  <option value="dtmf">DTMF — digit press routing</option>
                </select>
              </div>

              {mode === "ai" && (
                <div className="col-12 mb-3">
                  <label className="form-label">Entry-agent routing prompt</label>
                  <textarea className="form-control" rows="4" placeholder='e.g. "Greet the caller and ask whether they want sales or support. Hand off to the sales agent for pricing questions, or the support agent for technical issues."' {...register("prompt")} />
                  <small className="text-muted">AI mode usually just needs the entry agent's prompt to describe options — no separate routes table required.</small>
                </div>
              )}

              {mode === "dtmf" && (
                <div className="col-12 mb-3">
                  <label className="form-label">DTMF routes <span className="text-danger">*</span></label>
                  {fields.map((f, idx) => (
                    <div key={f.id} className="d-flex gap-2 mb-2 align-items-start">
                      <input className="form-control" style={{ maxWidth: 80 }} placeholder="Digit"
                        {...register(`routes.${idx}.digit`)} />
                      <input className="form-control" placeholder="Target agent slug (e.g. sales)"
                        {...register(`routes.${idx}.target`)} />
                      {fields.length > 1 && (
                        <button type="button" className="btn btn-outline-danger" onClick={() => remove(idx)}>−</button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => append({ digit: "", target: "" })}>+ Add Route</button>
                  <small className="d-block text-muted mt-1">
                    Target slugs must be reachable from the entry agent via <code>allowed_handoffs</code>.
                  </small>
                </div>
              )}

              <div className="col-12 form-check ms-2">
                <input className="form-check-input" type="checkbox" id="ivr_active" {...register("is_active")} />
                <label htmlFor="ivr_active" className="form-check-label">Active</label>
              </div>
            </div>
          </ModalBody>
          <Modal.Footer>
            <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? "Saving..." : "Save"}</button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
